// client/components/shared/EntityCardBlock.jsx
// Unified Card-Komponente für Projekte und Serviceaufträge.
// Eliminiert ~85% Duplikation zwischen ProjektCardBlock und ServiceCardBlock.
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../constants/api";
import { promise } from "../../utils/toast.js";
import StatusBadge from "../ui/StatusBadge";
import TypBadge from "../ui/TypBadge";
import ZuweisungBadge from "../ui/ZuweisungBadge";
import { fmtCHDate, fmtCHDateTime, buildRangeLabel, stop } from "../../utils/cardHelpers.js";
import { CalendarIcon, KundeMini } from "../forms/shared/FormAtoms.jsx";
import ArchiveReasonDialog from "../ui/ArchiveReasonDialog.jsx";
import AppButton from "../ui/AppButton.jsx";
import { entityCardArchiveButtonClassName } from "../../design/tokens.js";

/* ─── Portals ─────────────────────────────────────────────── */
function ArchiveDialog({
  open,
  onClose,
  onConfirm,
  entityName,
  entityLabel,
  loading
}) {
  return <ArchiveReasonDialog open={open} onClose={onClose} onConfirm={onConfirm} title={`${entityLabel} archivieren`} subtitle={entityName} loading={loading} placeholder="Kurz begruenden..." />;
}

/* ─── Main Component ──────────────────────────────────────── */
/**
 * EntityCardBlock — Unified Karte für Projekte & Serviceaufträge.
 *
 * Props:
 * - entity: Das Objekt (Projekt oder Service)
 * - entityType: "projekt" | "service" | "wartung"
 * - typ: Projekt-Typ (elektro, pv, ...) — nur bei Projekten
 * - onStatusChange: Callback
 * - metaRight: zusätzliche Meta-Elemente rechts
 * - ProgressComponent: Komponente für die Progress-Bar (optional)
 */
function EntityCardBlock({
  entity,
  entityType = "projekt",
  typ,
  onStatusChange,
  metaRight = null,
  ProgressComponent = null
}) {
    const navigate = useNavigate();
  const isProjekt = entityType === "projekt";
  const isWartung = entityType === "wartung";
  const [showArchive, setShowArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const statusBtnRef = useRef(null);

  // Entity Name
  const entityName = isProjekt ? entity?.name || "Unbenannt" : entity?.titel || (isWartung ? `Wartungsvertrag #${entity?.id}` : "Ohne Titel");
  const entityLabel = isProjekt ? "Projekt" : isWartung ? "Wartungsvertrag" : "Serviceauftrag";

  // Navigation path
  const detailPath = isProjekt ? `/projekte/${typ}/${entity?.id}` : isWartung ? `/wartungsvertraege/${entity?.id}` : `/service/${entity?.id}`;

  // Zugewiesene Mitarbeiter
  const mitarbeiter = useMemo(() => {
    const out = [];
    const seen = new Set();
    const add = u => {
      const name = String(u?.name || "").trim();
      if (!name) return;
      const id = Number(u?.id) || null;
      const avatar_url = u?.avatar_url || u?.avatar_path || null;
      const key = id ? `id:${id}` : `name:${name.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        id,
        name,
        avatar_url
      });
    };
    if (isProjekt) {
      if (Array.isArray(entity?.zugewiesene_users)) entity.zugewiesene_users.forEach(add);
      if (Array.isArray(entity?.zugewiesene)) entity.zugewiesene.forEach(add);
      if (!out.length) {
        String(entity?.zugewiesen_names || entity?.zugewiesen_name || "").split(",").map(s => s.trim()).filter(Boolean).forEach(name => add({
          name
        }));
      }
      return out;
    }
    if (isWartung) {
      if (entity?.verantwortlich_name || entity?.verantwortlich_user_id || entity?.verantwortlich_avatar_url) {
        add({
          id: entity?.verantwortlich_user_id,
          name: entity?.verantwortlich_name,
          avatar_url: entity?.verantwortlich_avatar_url
        });
      }
      if (entity?.zugewiesen_user_name) {
        add({
          id: entity?.zugewiesen_user_id,
          name: entity?.zugewiesen_user_name,
          avatar_url: entity?.zugewiesen_user_avatar_url
        });
      }
      return out;
    }
    // Service
    if (Array.isArray(entity?.zugewiesene_mitarbeiter)) entity.zugewiesene_mitarbeiter.forEach(add);
    if (!out.length && entity?.zugewiesen_name?.trim()) {
      entity.zugewiesen_name.split(",").map(x => x.trim()).filter(Boolean).forEach(name => add({
        name
      }));
    }
    return out;
  }, [entity, isProjekt, isWartung]);

  // Termin / Datumsbereich
  const {
    rangeLabel,
    dateTitle
  } = useMemo(() => {
    if (isProjekt) {
      const start = entity?.startdatum ? new Date(`${entity.startdatum}T12:00`) : null;
      const end = entity?.enddatum ? new Date(`${entity.enddatum}T12:00`) : null;
      const label = buildRangeLabel(start, end);
      return {
        rangeLabel: label,
        dateTitle: label ? `Zeitraum: ${label}` : ""
      };
    }
    if (isWartung) {
      const due = entity?.next_due_at ? new Date(`${entity.next_due_at}T12:00`) : null;
      const start = entity?.vertragsbeginn ? new Date(`${String(entity.vertragsbeginn).slice(0, 10)}T12:00`) : null;
      const dt = due || start || null;
      const label = dt ? fmtCHDate(dt) : null;
      return {
        rangeLabel: label,
        dateTitle: label ? `Fälligkeit/Beginn: ${label}` : ""
      };
    }
    // Service
    const termin = entity?.termin ? new Date(entity.termin) : null;
    const label = termin && !Number.isNaN(termin.getTime()) ? fmtCHDateTime(termin) : null;
    return {
      rangeLabel: label,
      dateTitle: label ? `Termin: ${label}` : ""
    };
  }, [entity, isProjekt, isWartung]);
  const wartungExtras = useMemo(() => {
    if (!isWartung) return [];
    const out = [];
    if (Number(entity?.laufzeit_monate) > 0) out.push(`Laufzeit ${Number(entity.laufzeit_monate)} M.`);
    if (Number.isFinite(Number(entity?.notify_days_before))) {
      out.push(`Reminder ${Math.max(0, Number(entity.notify_days_before))} Tg.`);
    }
    if (entity?.auto_renew === true) out.push("Auto-Renew");
    if (entity?.kuendigungstermin) {
      const d = new Date(`${String(entity.kuendigungstermin).slice(0, 10)}T12:00`);
      if (!Number.isNaN(d.getTime())) out.push(`Kündigung ${fmtCHDate(d)}`);
    }
    return out;
  }, [entity, isWartung]);

  // Archive
  const doArchive = useCallback(async grund => {
    try {
      setArchiving(true);
      const url = isProjekt ? `/projekte/archiv/${typ}/${entity?.id}/status` : isWartung ? `/wartungsvertraege/${entity?.id}` : `/service/${entity?.id}/status`;
      const body = isWartung ? {
        status: "ARCHIVIERT",
        notizen_intern: grund || null
      } : {
        status: "Archiviert",
        grund
      };
      await promise(api.put(url, body, {}), {
        loading: "Archiviere…",
        success: `${entityLabel} archiviert`,
        error: "Archivierung fehlgeschlagen"
      });
      onStatusChange?.(entity?.id, "Archiviert", grund);
      setShowArchive(false);
    } finally {
      setArchiving(false);
    }
  }, [entity?.id, typ, isProjekt, isWartung, entityLabel, onStatusChange]);

  // Modules Badge (zeigt aktive Module an)
  const moduleCount = Array.isArray(entity?.enabled_modules) ? entity.enabled_modules.length : 0;
  const wartungStatusRaw = String(entity?.status || "").toUpperCase();
  const wartungStatusLabel = {
    ENTWURF: "Entwurf",
    AKTIV: "Aktiv",
    PAUSIERT: "Pausiert",
    GEKUENDIGT: "Gekündigt",
    ABGELAUFEN: "Abgelaufen",
    ARCHIVIERT: "Archiviert"
  }[wartungStatusRaw] || "Entwurf";
  const wartungStatusCls = {
    ENTWURF: "bg-slate-500/20 text-slate-100 border-slate-400/40",
    AKTIV: "bg-green-500/20 text-green-100 border-green-400/40",
    PAUSIERT: "bg-amber-500/20 text-amber-100 border-amber-400/40",
    GEKUENDIGT: "bg-rose-500/20 text-rose-100 border-rose-400/40",
    ABGELAUFEN: "bg-violet-500/20 text-violet-100 border-violet-400/40",
    ARCHIVIERT: "bg-slate-600/30 text-slate-200 border-slate-300/40"
  }[wartungStatusRaw] || "bg-slate-500/20 text-slate-100 border-slate-400/40";
  const displayStatus = isWartung ? wartungStatusLabel : entity?.status;
  return <>
      <div onClick={e => {
      if (statusBtnRef.current?.contains(e.target)) return;
      if (e.target.closest?.("button, a, [role='menuitem']")) return;
      navigate(detailPath);
    }} className={`group interactive-card cursor-pointer text-left rounded-2xl px-4 py-3 border transition-all focus:outline-none focus-within:ring-2 ${"bg-surface border-default hover:!bg-surface focus-within:ring-blue-400/30"}`} role="button" tabIndex={0} onKeyDown={e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        navigate(detailPath);
      }
    }} aria-label={`${entityLabel} öffnen: ${entityName}`}>
        <div className="grid grid-cols-[1fr_auto] gap-2 sm:gap-3">
          {/* Links */}
          <div className="min-w-0">
            <h2 className={`text-[15px] sm:text-[17px] leading-5 sm:leading-6 font-semibold line-clamp-2 ${"text-blue-100"}`} title={entityName}>
              {entityName}
            </h2>
            <div className="mt-1">
              <KundeMini firma={entity?.kunden_firma} vorname={entity?.kunden_vorname} nachname={entity?.kunden_nachname} strasse={entity?.kunden_strasse} plz={entity?.kunden_plz} ort={entity?.kunden_ort} verwaltung_text={entity?.verwaltung_text} />
            </div>
            {isWartung ? <div className={`mt-1 text-[12px] line-clamp-1 ${"text-blue-200/75"}`} title={String(entity?.objekt_label || "").trim() || "Kein Objekt verknüpft"}>
                Objekt: {String(entity?.objekt_label || "").trim() || "—"}
              </div> : null}
            {isWartung && wartungExtras.length > 0 ? <div className="mt-1.5 flex flex-wrap gap-1.5">
                {wartungExtras.map(label => <span key={label} className={`text-[10px] px-2 py-0.5 rounded-full border ${"border-white/15 bg-white/5 text-blue-100/85"}`}>
                    {label}
                  </span>)}
              </div> : null}
          </div>

          {/* Rechts */}
          <div className="flex flex-col items-end gap-1 sm:gap-1.5 sm:w-[260px] md:w-[280px]">
            {rangeLabel ? <div className={`text-[12px] flex items-center gap-1.5 min-w-0 ${"text-blue-200/70"}`} title={dateTitle}>
                <CalendarIcon />
                <span className="truncate">{rangeLabel}</span>
              </div> : <div className={`text-[12px] ${"text-blue-200/70"}`}>
                &nbsp;
              </div>}

            <div className="mt-0.5 flex flex-wrap gap-1.5 justify-end items-center min-h-[26px]">
              {isWartung && (!mitarbeiter || mitarbeiter.length === 0) ? <span className={`text-[11px] tabular-nums ${"text-blue-200/45"}`} title="Projektleiter / Erstellung">
                  Verantwortlich: —
                </span> : null}
              {(mitarbeiter || []).slice(0, 3).map((u, i) => <ZuweisungBadge key={u?.id ?? `${u?.name || "u"}-${i}`} user={u} variant="full" size="xs" truncateAt={16} />)}
              {(mitarbeiter || []).length > 3 && <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs border transition-all ${"bg-white/10 text-blue-100 border-white/10"}`} title={mitarbeiter.slice(3).map(u => u?.name).filter(Boolean).join(", ")}>
                  +{mitarbeiter.length - 3}
                </span>}
            </div>

          </div>

          <div className="col-span-2 flex items-center justify-between gap-2 min-w-0 pt-0.5">
            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
              {metaRight}
              {isProjekt && entity?.untertyp ? (
                <TypBadge typ={typ} onlySuffix size="status" suffix={entity.untertyp} title={entity.untertyp} />
              ) : null}
              {!isWartung && moduleCount > 0 ? (
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${"bg-blue-400/50"}`}
                  title={`${moduleCount} Zusatzbereich${moduleCount > 1 ? "e" : ""} aktiv`}
                />
              ) : null}
              <span title={isWartung ? "Wartungsstatus" : "Status (via Checkliste gesteuert)"}>
                {isWartung ? (
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${wartungStatusCls}`}>
                    {displayStatus}
                  </span>
                ) : (
                  <StatusBadge status={displayStatus} />
                )}
              </span>
            </div>
            {(isWartung ? String(entity?.status || "").toUpperCase() !== "ARCHIVIERT" : entity?.status !== "Archiviert") ? (
              <AppButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={archiving}
                onClick={e => {
                  stop(e);
                  setShowArchive(true);
                }}
                className={`${entityCardArchiveButtonClassName} shrink-0`}
                title={`${entityLabel} archivieren`}
              >
                Archivieren
              </AppButton>
            ) : (
              <span className="shrink-0" aria-hidden />
            )}
          </div>

          {/* Progress */}
          {ProgressComponent && <div className="col-span-2 pt-1">
              <ProgressComponent {...isProjekt ? {
            projektId: entity?.id,
            typ
          } : {
            serviceId: entity?.id
          }} height={6} showLabel className="w-full opacity-80" />
            </div>}
        </div>
      </div>

      {/* Archive Dialog */}
      <ArchiveDialog open={showArchive} onClose={() => setShowArchive(false)} onConfirm={doArchive} entityName={entityName} entityLabel={entityLabel} loading={archiving} />
    </>;
}
export default memo(EntityCardBlock);